import { format as formatNumber } from 'numerable';
import type { ContentItemWithAuthorModel } from '@domain/models/ContentItemWithAuthorModel';
import type { CollectionModel } from '@domain/models/CollectionModel';
import type { CreatorModel } from '@domain/models/CreatorModel';

const count = (value?: number | null): string => formatNumber(value ?? 0, '0 a');

const BRAND = 'Jigsaw Puzzle Universe';
const TITLE_MAX = 70;

// Builds a unique, brand-suffixed <title> and degrades gracefully so it stays
// within the ~70-char target even for long puzzle names.
export const buildPuzzleTitle = (puzzle: ContentItemWithAuthorModel): string => {
	const base = `${puzzle.name} jigsaw puzzle`;
	const withAuthor = puzzle.author?.name ? `${base} by ${puzzle.author.name}` : base;

	const full = `${withAuthor} | ${BRAND}`;
	if (full.length <= TITLE_MAX) {
		return full;
	}

	const baseBranded = `${base} | ${BRAND}`;
	if (baseBranded.length <= TITLE_MAX) {
		return baseBranded;
	}

	return base.length <= TITLE_MAX ? base : `${base.slice(0, TITLE_MAX - 1).trimEnd()}…`;
};

// Appends the brand, or clamps to the ~70-char target if the base is too long.
const brandedTitle = (base: string): string => {
	const full = `${base} | ${BRAND}`;
	if (full.length <= TITLE_MAX) {
		return full;
	}
	return base.length <= TITLE_MAX ? base : `${base.slice(0, TITLE_MAX - 1).trimEnd()}…`;
};

export const buildCategoryTitle = (category: CollectionModel): string =>
	brandedTitle(`${category.name} Jigsaw Puzzles`);

export const buildCreatorTitle = (creator: CreatorModel): string => brandedTitle(`${creator.name}'s Jigsaw Puzzles`);

// Deterministic, answer-first body copy built from structured fields.
// Used directly for the puzzle long tail, and as the fallback for any entity
// that has no LLM-generated description yet (see [[seoText]]).

export const buildPuzzleIntro = (puzzle: ContentItemWithAuthorModel): string => {
	const authorName = puzzle.author?.name;
	const keywords =
		puzzle.keywords
			?.map((keyword) => keyword?.trim())
			.filter(Boolean)
			.slice(0, 6) ?? [];

	let intro = `${puzzle.name} is a digital jigsaw puzzle`;
	if (authorName) {
		intro += ` by ${authorName}`;
	}
	intro += ' on Jigsaw Puzzle Universe';
	if (keywords.length) {
		intro += `, featuring ${keywords.join(', ')}`;
	}
	intro += '.';

	intro += ' Play it free in the Jigsaw Puzzle Universe app on the App Store and Google Play.';

	return intro;
};

export const buildCategoryIntro = (category: CollectionModel): string => {
	const number = category.numberOfPuzzles;
	const amount = number ? `${count(number)} ` : '';
	return (
		`${category.name} jigsaw puzzles is a collection of ${amount}digital adult jigsaw puzzles ` +
		`from talented creators on Jigsaw Puzzle Universe. Browse the ${category.name} collection and ` +
		'play any puzzle free in the mobile app, available on the App Store and Google Play.'
	);
};

export const buildCreatorIntro = (creator: CreatorModel): string => {
	const parts: string[] = [];
	if (creator.puzzlesCount) {
		parts.push(`${count(creator.puzzlesCount)} puzzles`);
	}
	if (creator.followersCount) {
		parts.push(`${count(creator.followersCount)} followers`);
	}
	const stats = parts.length ? ` with ${parts.join(' and ')}` : '';
	return (
		`${creator.name} is a digital jigsaw puzzle creator on Jigsaw Puzzle Universe${stats}. ` +
		`Explore ${creator.name}'s puzzles and play them free in the mobile app, ` +
		'available on the App Store and Google Play.'
	);
};
